import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatDate, sortSeries } from './utils';
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { DotsVerticalIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import ErrorDisplayPopup from './errorDisplayPopup';
import DicomDetailsPopup from './dicomDetailsPopup';

const StudyInfoTable = (props: any) => {
  const {
    hierarchicalMetadata,
    handleClearButton,
    seriesDataToShow,
    openGroupedSeriesRow,
    toggleRow,
    openRows,
    validatorJson,
    handleOpenRow,
    disabled,
  } = props;

  const errorDisplayTooltip = () => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <InfoCircledIcon className="w-5 h-5 text-yellow-500 "></InfoCircledIcon>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-muted-foreground">Queued for upload</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const popupDisplay = (children: any, width: any) => {
    return (
      <Popover>
        <PopoverTrigger>
          <DotsVerticalIcon className="w-4 h-4 text-foreground"></DotsVerticalIcon>
        </PopoverTrigger>
        <PopoverContent
          className={`w-[${width}] mr-6 max-h-[70vh] shadow-xl shadow-slate-500 overflow-y-auto`}
        >
          {children}
        </PopoverContent>
      </Popover>
    );
  };
  return (
    <div className="w-full bg-muted text-foreground p-4 mb-4 rounded-lg border border-border [&_th]:text-foreground [&_td]:text-foreground">
      <div className="flex w-full justify-between">
        <span className="text-3xl font-medium text-foreground">Validation</span>
        <div className="gap-1 flex ">
          {hierarchicalMetadata?.studies?.length && (
            <Button
              className="rounded bg-[#1e1b3c] text-white w-[100px]"
              onClick={() => handleClearButton()}
              disabled={disabled}
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      <div className="mt-4 h-auto">
        <Table className="border-2 border-border text-foreground">
          <TableHeader className="border-b border-border">
            <TableRow className="bg-accent text-foreground">
              <TableHead className="w-1/6">Size</TableHead>
              <TableHead className="w-2/6">Study Date</TableHead>
              <TableHead className="w-3/6">Study Instance ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hierarchicalMetadata?.studies?.map((data: any) => {
              return (
                <TableRow key={data?.StudyInstanceUID}>
                  <TableCell
                    className="font-medium text-ellipsis whitespace-nowrap text-left max-w-[110px] overflow-hidden"
                    title="value"
                  >
                    {data?.size}
                  </TableCell>
                  <TableCell className="font-medium text-ellipsis whitespace-nowrap text-left max-w-[100px] overflow-hidden">
                    {formatDate(data?.StudyDate)}
                  </TableCell>
                  <TableCell className="font-medium text-ellipsis whitespace-nowrap text-left max-w-[100px] overflow-hidden">
                    {data?.StudyInstanceUID}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {!hierarchicalMetadata && (
          <div className="flex justify-center items-center h-full w-full">
            <span className="text-muted-foreground">No data available</span>
          </div>
        )}
        <div className="w-full">
          {hierarchicalMetadata?.studies?.length > 0 && (
            <div className="relative">
              <div className="overflow-x-auto">
                <div className="overflow-y-auto mb-8">
                  <Table className="border-2 border-border text-foreground">
                    <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
                      <TableRow className="bg-accent text-foreground">
                        <TableHead></TableHead>
                        <TableHead></TableHead>
                        <TableHead className="w-1/3">
                          Series Description
                        </TableHead>
                        <TableHead className="w-1/3">Modality</TableHead>
                        <TableHead className="w-1/3">
                          Series Instance UID
                        </TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    {seriesDataToShow?.result?.map((res: any) => (
                      <>
                        <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
                          <TableRow className="bg-muted text-foreground">
                            <Button
                              variant="ghost"
                              size="sm"
                              key={res?.groupValue}
                              onClick={() => handleOpenRow(res.groupValue)}
                            >
                              {openGroupedSeriesRow[res?.groupValue] ? (
                                <ChevronDown />
                              ) : (
                                <ChevronRight />
                              )}
                            </Button>
                            <TableHead colSpan={5}>{res?.groupValue}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody
                          className="[&_tr:last-child]:border-1"
                          key={res?.groupValue}
                        >
                          <React.Fragment>
                            {sortSeries(res?.item).map((value: any) => {
                              return (
                                <>
                                  {openGroupedSeriesRow[res?.groupValue] && (
                                    <TableRow
                                      className={`${
                                        value?.isDuplicate
                                          ? 'border-border border-2 bg-card text-muted-foreground font-normal'
                                          : ''
                                      }
                            ${
                              value?.isInvalidDicom
                                ? 'border-border border-2 bg-card text-muted-foreground font-normal'
                                : ''
                            } font-semibold text-foreground`}
                                      key={value?.SeriesInstanceUID}
                                    >
                                      <TableCell>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            toggleRow(value?.SeriesInstanceUID)
                                          }
                                          key={value?.SeriesInstanceUID}
                                        >
                                          {openRows[
                                            value?.SeriesInstanceUID
                                          ] ? (
                                            <ChevronDown />
                                          ) : (
                                            <ChevronRight />
                                          )}
                                        </Button>
                                      </TableCell>
                                      <TableCell>
                                        {value?.isDuplicate === true ||
                                        value?.isInvalidDicom === true
                                          ? errorDisplayTooltip()
                                          : null}
                                      </TableCell>
                                      <TableCell>
                                        {value?.SeriesDescription}
                                      </TableCell>
                                      <TableCell>{value?.Modality}</TableCell>
                                      <TableCell>
                                        {value?.SeriesInstanceUID}
                                      </TableCell>
                                      <TableCell>
                                        {popupDisplay(
                                          <DicomDetailsPopup props={value} />,
                                          '500px'
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                  {openRows[value?.SeriesInstanceUID] && (
                                    <TableRow
                                      className={`${
                                        value?.isDuplicate
                                          ? 'border-border border-2 bg-muted/50 text-muted-foreground'
                                          : ''
                                      }
                          ${
                            value?.isInvalidDicom
                              ? 'border-border border-2 bg-muted/50 text-muted-foreground'
                              : ''
                          } text-foreground`}
                                    >
                                      <TableHead colSpan={3} align="center">
                                        Instance Number
                                      </TableHead>
                                      <TableHead align="center">
                                        Modality
                                      </TableHead>
                                      <TableHead colSpan={2} align="center">
                                        Study Instance UID
                                      </TableHead>
                                    </TableRow>
                                  )}
                                  {openRows[value?.SeriesInstanceUID] &&
                                    value?.instances
                                      ?.sort(
                                        (a: any, b: any) =>
                                          a.metadata?.InstanceNumber -
                                          b.metadata?.InstanceNumber
                                      )
                                      ?.map((data: any) => {
                                        return (
                                          <TableRow
                                            key={data?.metadata?.SOPInstanceUID}
                                            className={`${
                                              value?.isDuplicate
                                                ? 'border-border border-2 bg-muted/50 text-muted-foreground'
                                                : ''
                                            }
                                                ${
                                                  value.isInvalidDicom
                                                    ? 'border-border border-2 bg-muted/50 text-muted-foreground'
                                                    : ''
                                                } text-foreground`}
                                          >
                                            <TableCell colSpan={3}>
                                              {data?.metadata?.InstanceNumber}
                                            </TableCell>
                                            <TableCell>
                                              {data?.metadata?.Modality}
                                            </TableCell>
                                            <TableCell>
                                              {data?.metadata?.StudyInstanceUID}
                                            </TableCell>
                                            <TableCell>
                                              {popupDisplay(
                                                <ErrorDisplayPopup
                                                  instanceData={data}
                                                  validatorJson={validatorJson}
                                                  studyData={value}
                                                />,
                                                '1000px'
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                </>
                              );
                            })}
                          </React.Fragment>
                        </TableBody>
                      </>
                    ))}
                    <TableBody className="[&_tr:last-child]:border-1">
                      <React.Fragment>
                        {sortSeries(seriesDataToShow?.nonGroupedData).map(
                          (element: any) => {
                            const isError =
                              element?.isDuplicate === true ||
                              element?.isInvalidDicom === true;
                            return (
                              <>
                                <TableRow
                                  className={`font-semibold
                                    ${
                                      isError
                                        ? 'bg-muted/50 text-muted-foreground font-normal'
                                        : 'text-foreground'
                                    }
                                  `}
                                  key={element?.SeriesInstanceUID}
                                >
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        toggleRow(element?.SeriesInstanceUID)
                                      }
                                      key={element?.SeriesInstanceUID}
                                    >
                                      {openRows[element?.SeriesInstanceUID] ? (
                                        <ChevronDown />
                                      ) : (
                                        <ChevronRight />
                                      )}
                                    </Button>
                                  </TableCell>
                                  <TableCell>
                                    {element?.isDuplicate === true ||
                                    element?.isInvalidDicom === true
                                      ? errorDisplayTooltip()
                                      : ''}
                                  </TableCell>
                                  <TableCell>
                                    {element?.SeriesDescription}
                                  </TableCell>
                                  <TableCell>{element?.Modality}</TableCell>
                                  <TableCell>
                                    {element?.SeriesInstanceUID}
                                  </TableCell>
                                  <TableCell>
                                    {popupDisplay(
                                      <DicomDetailsPopup props={element} />,
                                      '500px'
                                    )}
                                  </TableCell>
                                </TableRow>
                                {openRows[element?.SeriesInstanceUID] && (
                                  <TableRow
                                    className={`border-2 bg-muted/50 text-foreground 
                                      ${
                                        isError
                                          ? 'border-border text-muted-foreground'
                                          : 'border-green-500'
                                      }
                                    `}
                                  >
                                    <TableHead colSpan={3} align="center">
                                      Instance Number
                                    </TableHead>
                                    <TableHead align="center">
                                      Modality
                                    </TableHead>
                                    <TableHead colSpan={2} align="center">
                                      Study Instance UID
                                    </TableHead>
                                  </TableRow>
                                )}
                                {openRows[element?.SeriesInstanceUID] &&
                                  element?.instances
                                    ?.sort(
                                      (a: any, b: any) =>
                                        a.metadata?.InstanceNumber -
                                        b.metadata?.InstanceNumber
                                    )
                                    .map((data: any) => {
                                      return (
                                        <TableRow
                                          key={data?.metadata?.StudyInstanceUID}
                                          className={`border-2 bg-muted/50 text-foreground 
                                            ${
                                              isError
                                                ? 'border-border text-muted-foreground'
                                                : 'border-green-500'
                                            }
                                          `}
                                        >
                                          <TableCell colSpan={3}>
                                            {data?.metadata?.InstanceNumber}
                                          </TableCell>
                                          <TableCell>
                                            {data?.metadata?.Modality}
                                          </TableCell>
                                          <TableCell>
                                            {data?.metadata?.StudyInstanceUID}
                                          </TableCell>
                                          <TableCell>
                                            {popupDisplay(
                                              <ErrorDisplayPopup
                                                instanceData={data}
                                                validatorJson={validatorJson}
                                                studyData={element}
                                              />,
                                              '1000px'
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                              </>
                            );
                          }
                        )}
                      </React.Fragment>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudyInfoTable;
